import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { WorkspaceViewSaveService } from '../../services/workspace-view-save.service';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Overview } from '../../abstract/Overview';
import FileSaver from 'file-saver';
import { INeo4j } from '../../interfaces/Neo4j';
import { FormsModule } from '@angular/forms';
import { CLOSE, CREATE_NEW_VIEW, DELETE_VIEW, ERROR, EXPORT_VIEW, IMPORT_VIEW, LOADING_VIEWS, PLACEHOLDER_VIEW_NAME, RESTORE_A_VIEW, RESTORE_HERE, RESTORE_NEW_TAB, SAVE_IN_VIEW, SAVE_VIEW_BUTTON, VIEWS } from '../../shared/strings';
import { Importer } from '../../clases/Importer';
import { convertIntoImporterType, generateCurrentDate, generateUUID } from '../../utils/utils';
import { UserService } from '../../services/user.service';
import { ServerResponse } from '../../interfaces/ServerResponse';
import { ViewFromServer } from '../../interfaces/ViewFromServer';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CdkMenu, CdkMenuModule } from '@angular/cdk/menu';
import { ImportEnum } from '../../enums/Import';
import { SaveView } from '../../clases/SaveView';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { SharedService } from '../../services/shared.service';
import { UpdateView } from '../../clases/UpdateView';
import { Exporter } from '../../clases/Exporter';
import { NotifyDialogService } from '../../services/notify-dialog.service';
import { DataViewer } from '../../abstract/DataViewer';
import { ISaveView } from '../../interfaces/SaveView';
import { INeo4jData } from '../../interfaces/INeo4jData';
import { Neo4jCreatorComponent } from '../tab-models/neo4j-creator/neo4j-creator.component';
import { SchemaTreeComponent } from '../schema-tree/schema-tree.component';
import { SchemaTree } from '../../clases/SchemaTree';
import { BeNeo4jService } from '../../services/be-neo4j.service';
import { MainComponent } from '../../pages/main/main.component';

@Component({
  selector: 'views-overview',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    MatIconModule,
    MatProgressBarModule,
    CdkMenuModule
  ],
  templateUrl: './views-overview.component.html',
  styleUrl: './views-overview.component.scss',
})
export class ViewsOverviewComponent extends Overview implements OnInit, AfterViewInit{

  //String definitions
  VIEWS = VIEWS;
  RESTORE_A_VIEW = RESTORE_A_VIEW;
  CREATE_NEW_VIEW = CREATE_NEW_VIEW;
  SAVE_VIEW_BUTTON = SAVE_VIEW_BUTTON;
  SAVE_IN_VIEW = SAVE_IN_VIEW;
  RESTORE_HERE = RESTORE_HERE;
  RESTORE_NEW_TAB = RESTORE_NEW_TAB;
  DELETE_VIEW = DELETE_VIEW;
  PLACEHOLDER_VIEW_NAME = PLACEHOLDER_VIEW_NAME;
  LOADING_VIEWS = LOADING_VIEWS;
  CLOSE = CLOSE;
  IMPORT_VIEW = IMPORT_VIEW;
  ERROR = ERROR;
  EXPORT_VIEW = EXPORT_VIEW;

  name: string = "";
  file!: Blob;

  fileContent: string | ArrayBuffer | null = null;
  _currentComponent!: DataViewer | null;
  
  @Input() mainComponent!: MainComponent;
  @Input() importData!: EventEmitter<Importer>;

  @ViewChild('input') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(SchemaTreeComponent) treeComponent!: SchemaTreeComponent;

  views!: ViewFromServer[];
  currentView!: ViewFromServer | null;
  tabView!: ViewFromServer | null;

  loadingViews: boolean = true; 

  treeInfo: SchemaTree[] = [];

  constructor(
    private userService: UserService,
    private sharedService: SharedService,
    private notifyDialogService: NotifyDialogService,
    private beNeo4jService: BeNeo4jService
  ){ super(); }

  ngOnInit(): void {
    this.isOverviewActive = false;
  }

  ngAfterViewInit(): void {
    this.getViews();
  }

  getViews(){
    this.loadingViews = true;
    this.userService.getViews().subscribe({
      next: (res: ServerResponse<ViewFromServer>)=>{
        this.views = res.result;
        for (const view of this.views) {
          view.file_content = JSON.parse(view.file_content as string) as ISaveView;
          view.file_content.name_value = view.file_name;
        }
        this.loadingViews = false;
      },
      error: (error)=>{
        this.loadingViews = false;
      }
    });
  }

  private readonly workspaceSave = inject(WorkspaceViewSaveService);

  onSave(){ this.saveWorkspace(true); }

  onUpdate(){ this.saveWorkspace(false); }

  private saveWorkspace(saveAs: boolean): void {
    const owner = this._currentComponent;
    if (!owner) return;
    const lifecycle = owner.agentContextRevision;
    void this.workspaceSave.save(owner, { name: this.name, saveAs,
      isCurrent: () => owner.agentContextRevision === lifecycle && this.mainComponent.components.toArray().includes(owner)
    }).then(() => this.getViews()).catch(() => {
      this.notifyDialogService.openDialog('Save failed', 'The View could not be saved. Check the workspace and try again.');
    });
  }

  saveFile(connection: INeo4j){
    let exported = {
      data_service_id: connection.connection.dataService.data_service_id,
      containers: connection.containers,
      name_value: connection.name_value
    } as ISaveView;
    const viewIdentity = this.getViewIdentity(connection);
    let saveView = new SaveView(this.name, JSON.stringify(exported), viewIdentity.hierarchy, viewIdentity.modelUUID);
    this.onSendFile(saveView);
  }

  onSendFile(saveView: SaveView) {
    this.userService.saveView(saveView).subscribe({
      next: (res: ServerResponse<ViewFromServer>) => {
        this.getViews();
        let view = res.result[0];
        this.setView(view);
        if (!this._currentComponent) return;
        this._currentComponent.setViewFromServer(view);
      }
    });
  }

  updateFile(connection: INeo4j, view: ViewFromServer){
    let exported = {
      data_service_id: connection.connection.dataService.data_service_id,
      containers: connection.containers,
      name_value: connection.name_value
    } as ISaveView;
    const viewIdentity = this.getViewIdentity(connection);
    this.userService.modifyView(new UpdateView(view.file_name, JSON.stringify(exported), view.viewUUID, viewIdentity.hierarchy, viewIdentity.modelUUID)).subscribe({
      next: (res: any)=>{
        this.getViews();
      }
    });
  }

  private getViewIdentity(connection: INeo4j) {
    const hierarchies = new Set<string>();
    const modelUUIDs = new Set<string>();

    for (const container of connection.containers || []) {
      if (container.hierarchy) hierarchies.add(container.hierarchy);
      if (container.model_UUID) modelUUIDs.add(container.model_UUID);
    }

    return {
      hierarchy: hierarchies.size === 1 ? Array.from(hierarchies)[0] : undefined,
      modelUUID: modelUUIDs.size === 1 ? Array.from(modelUUIDs)[0] : undefined
    };
  }

  deleteFile() {
    this.notifyDialogService.openDialog("Are you sure?", "are you sure to delete the view?", true, () => {
      this.userService.deleteView(this.currentView!.viewUUID).subscribe({
        next: (res: any) => {
          this.getViews();
        }
      });
    });
  }

  onImportView(){
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const json = JSON.parse(reader.result as string) as ViewFromServer;
          let saveView = new SaveView(json.file_name, json.file_content as string, json.hierarchy, json.model_UUID);
          this.onSendFile(saveView);
        } catch (error) {
          this.notifyDialogService.openDialog(ERROR, 'Invalid JSON file: ' + error);
        }
      };

      reader.onerror = () => {
        this.notifyDialogService.openDialog(ERROR, 'File reading error: ' + reader.error);
      };

      reader.readAsText(file);
    }
  }

  onDownloadView(){
    if(this.currentView){
      let copy = JSON.parse(JSON.stringify(this.currentView))as ViewFromServer;
      copy.file_content = JSON.stringify(copy.file_content);
      const jsonBlob = new Blob([JSON.stringify(copy, null, 2)], { type: 'application/json' });
      let finalName = `DXC_TWS_VIEW_[${this.currentView.file_name}]_${generateCurrentDate()}_${generateUUID()}`;
      FileSaver.saveAs(jsonBlob, finalName);
    }
  }

  onRestore(importType: string, menu: CdkMenu) {
    if(!this.currentView)return;
    let type = convertIntoImporterType(importType);
    menu.menuStack.closeAll();
    this.importData.emit(this.createImporter(this.currentView, type));
  }

  /** Agent entry point: resolve a saved View and reuse the View Library restore behavior. */
  async restoreAgentView(viewName: string, destination: 'CURRENT_CREATOR' | 'NEW_TAB'): Promise<void> {
    // Always resolve an Agent restore against the backend. `this.views` is a
    // UI cache and may predate a View that was just saved by the Agent; opening
    // the View Library used to be the only way to refresh it.
    const response = await firstValueFrom(this.userService.getViews());
    const normalized = (Array.isArray(response.result) ? response.result : [])
      .map(view => this.normalizeView(view));
    this.views = normalized;
    const requested = viewName.trim().replace(/\.json$/i, '');
    const exact = normalized.filter(view => view.file_name.replace(/\.json$/i, '') === requested);
    // The library appends the Data Service in square brackets to some saved
    // View names. Accept only that native display suffix, never a broad prefix.
    const matches = exact.length ? exact : normalized.filter(view =>
      view.file_name.replace(/\.json$/i, '').replace(/\s+\[[^\]]+\]$/, '') === requested);
    if (matches.length !== 1) throw new Error(matches.length ? 'View name is ambiguous' : 'View was not found');
    const type = destination === 'NEW_TAB' ? ImportEnum.IMPORT_NEW_TAB : ImportEnum.IMPORT_IN_VIEW;
    await this.mainComponent.restoreAgentView(this.createImporter(matches[0], type), destination);
  }

  private normalizeView(view: ViewFromServer): ViewFromServer {
    const normalized = { ...view, file_content: typeof view.file_content === 'string'
      ? JSON.parse(view.file_content) : JSON.parse(JSON.stringify(view.file_content)) };
    (normalized.file_content as ISaveView).name_value = normalized.file_name;
    return normalized;
  }

  private createImporter(view: ViewFromServer, type: ImportEnum): Importer {
    let savedView = (view.file_content as ISaveView);
    let dataservice = this.sharedService.data_services!.find((service)=>service.data_service_id === savedView.data_service_id);
    let neo4j_data = {
      dataService: dataservice,
      DBlabels: [],
      DBrelations: [],
      hierarchy: [],
      schemaTree: []
    } as INeo4jData;
    let neo4j = {
      connection: neo4j_data,
      name_value: savedView.name_value,
      containers: JSON.parse(JSON.stringify(savedView.containers)),
      model_UUID: view.model_UUID,
      hierarchy: view.hierarchy
    } as INeo4j;
    return new Importer(neo4j, type, view);
  }

  createFinalName(name: string): string{
    let final = name.replaceAll(" ", "_");
    if(!final.includes(".json")) final = final+".json";
    return final;
  }


  onViewSelected(view: ViewFromServer){
    this.currentView = view;
  }

  isSameDataService(): boolean{
    if(!this.currentView)return false;
    let connection = this.currentView.file_content as ISaveView;
    return connection.data_service_id === this.sharedService.data_service?.data_service_id
  }

  getViewServiceName(view: ViewFromServer): string{
    let id = (view.file_content as ISaveView).data_service_id;
    let dataservice = this.sharedService.data_services!.find((service)=>service.data_service_id === id);
    return (dataservice) ? dataservice?.data_service_name : "";
  }

  setView=(view: ViewFromServer)=>{
    this.tabView = view;
  }

  canSaveView(){
    if(this._currentComponent instanceof  Neo4jCreatorComponent){
      return true;
    }
    return false;
  }

  setCurrentComponent(component: DataViewer){
    this._currentComponent = component;
  }
}
